import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { formatTimeWithTz } from "@/lib/order-time";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import type { DuplicateOrderRow } from "@/hooks/useDuplicateOrderPairs";
import { useMergeOrders } from "@/hooks/useMergeOrders";
import { OrderComparisonCard } from "@/components/dashboard/OrderComparisonCard";

type FieldKey =
  | "tender_id"
  | "billing_party_id"
  | "ship_from_location_id"
  | "ship_to_location_id"
  | "ready_time"
  | "ready_time_end"
  | "requested_delivery_time"
  | "requested_delivery_time_end"
  | "trailer_type_id"
  | "trip_type"
  | "notes";

const FIELDS: { key: FieldKey; label: string; kind: "text" | "time" | "id" }[] = [
  { key: "tender_id", label: "Tender ID", kind: "text" },
  { key: "billing_party_id", label: "Billing party", kind: "id" },
  { key: "ship_from_location_id", label: "Origin", kind: "id" },
  { key: "ship_to_location_id", label: "Destination", kind: "id" },
  { key: "ready_time", label: "Ready time", kind: "time" },
  { key: "ready_time_end", label: "Ready time (end)", kind: "time" },
  { key: "requested_delivery_time", label: "Delivery time", kind: "time" },
  { key: "requested_delivery_time_end", label: "Delivery time (end)", kind: "time" },
  { key: "trailer_type_id", label: "Trailer type", kind: "id" },
  { key: "trip_type", label: "Trip type", kind: "text" },
  { key: "notes", label: "Notes", kind: "text" },
];

function displayValue(o: DuplicateOrderRow, key: FieldKey, viewerTz: string): string {
  const v = (o as any)[key];
  if (v == null || v === "") return "—";
  if (key === "ship_from_location_id") return o.ship_from_location?.short_code ?? String(v);
  if (key === "ship_to_location_id") return o.ship_to_location?.short_code ?? String(v);
  if (key.endsWith("_time") || key.endsWith("_time_end")) {
    return formatTimeWithTz(String(v), viewerTz, "MMM d, HH:mm") || String(v);
  }
  return String(v);
}

function rawValue(o: DuplicateOrderRow, key: FieldKey): string | null {
  const v = (o as any)[key];
  return v == null ? null : String(v);
}

export function MergeOrdersDialog({
  open,
  onOpenChange,
  keeper,
  merged,
  onMerged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keeper: DuplicateOrderRow;
  merged: DuplicateOrderRow;
  onMerged?: () => void;
}) {
  const mergeMutation = useMergeOrders();
  const viewerTz = useViewerTimezone();

  // Detect differing fields and default to non-null side (keeper preferred on tie).
  const differences = useMemo(() => {
    return FIELDS.filter((f) => rawValue(keeper, f.key) !== rawValue(merged, f.key));
  }, [keeper, merged]);

  const [choices, setChoices] = useState<Record<string, "keeper" | "merged">>(() => {
    const initial: Record<string, "keeper" | "merged"> = {};
    for (const f of differences) {
      const kv = rawValue(keeper, f.key);
      const mv = rawValue(merged, f.key);
      // If keeper is null but merged has a value, take merged; else keeper.
      initial[f.key] = kv == null && mv != null ? "merged" : "keeper";
    }
    return initial;
  });

  const handleConfirm = async () => {
    const overrides: Record<string, string | null> = {};
    for (const f of differences) {
      const pick = choices[f.key] === "merged" ? merged : keeper;
      const val = rawValue(pick, f.key);
      // Only send overrides that actually change the keeper's current value.
      if (val !== rawValue(keeper, f.key)) {
        overrides[f.key] = val;
      }
    }
    await mergeMutation.mutateAsync({
      keptId: keeper.id,
      mergedId: merged.id,
      overrides,
    });
    onOpenChange(false);
    onMerged?.();
  };

  // Live preview of the record that survives the merge.
  const previewOrder = useMemo<DuplicateOrderRow>(() => {
    const next: DuplicateOrderRow = { ...keeper };
    for (const f of differences) {
      if (choices[f.key] !== "merged") continue;
      (next as any)[f.key] = (merged as any)[f.key];
      if (f.key === "ship_from_location_id") next.ship_from_location = merged.ship_from_location;
      if (f.key === "ship_to_location_id") next.ship_to_location = merged.ship_to_location;
      if (f.key === "billing_party_id") next.billing_party = merged.billing_party;
    }
    return next;
  }, [keeper, merged, differences, choices]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            Merge Orders {keeper.unique_id ?? keeper.id.slice(0, 8)} and{" "}
            {merged.unique_id ?? merged.id.slice(0, 8)}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col rounded-lg border lg:flex-row lg:items-stretch lg:divide-x">
          {/* Left column height is driven by the Preview column on the right */}
          <div className="relative min-h-0 lg:w-1/2 lg:shrink-0 lg:overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto lg:absolute lg:inset-0 lg:max-h-none">
          <div className="sticky top-0 z-10 bg-background px-4 pt-4 pb-2">
            <div className="text-base font-semibold">Compare differences</div>
            <p className="text-xs text-muted-foreground mt-2">
              These fields differ between the duplicate orders. Select the values to keep in the merged order.
            </p>
          </div>
          <div className="px-4 pb-4">
          {differences.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              These orders have identical values on all mergeable fields. Confirming will simply cancel{" "}
              {merged.unique_id ?? merged.id.slice(0, 8)} and reparent its children.
            </p>
          ) : (
            <div className="flex flex-col divide-y rounded-lg border bg-card">
              {differences.map((f) => (
                <div key={f.key} className="p-3">
                  <div className="text-sm font-medium mb-2">{f.label}</div>
                  <RadioGroup
                    value={choices[f.key]}
                    onValueChange={(v) => setChoices((c) => ({ ...c, [f.key]: v as "keeper" | "merged" }))}
                    className="grid grid-cols-2 gap-2"
                  >
                    <Label
                      htmlFor={`${f.key}-keeper`}
                      className={`flex items-start gap-2 rounded border p-2 cursor-pointer hover:bg-muted/50 ${
                        choices[f.key] === "keeper" ? "border-primary bg-primary/10 hover:bg-primary/10" : ""
                      }`}
                    >
                      <RadioGroupItem value="keeper" id={`${f.key}-keeper`} className="mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-muted-foreground font-mono">{keeper.unique_id}</div>
                        <div className="text-sm">{displayValue(keeper, f.key, viewerTz)}</div>
                      </div>
                    </Label>
                    <Label
                      htmlFor={`${f.key}-merged`}
                      className={`flex items-start gap-2 rounded border p-2 cursor-pointer hover:bg-muted/50 ${
                        choices[f.key] === "merged" ? "border-primary bg-primary/10 hover:bg-primary/10" : ""
                      }`}
                    >
                      <RadioGroupItem value="merged" id={`${f.key}-merged`} className="mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-muted-foreground font-mono">{merged.unique_id}</div>
                        <div className="text-sm">{displayValue(merged, f.key, viewerTz)}</div>
                      </div>
                    </Label>
                  </RadioGroup>
                </div>
              ))}
            </div>
          )}
          </div>
          </div>
          </div>

          <div className="flex flex-col gap-2 p-4 lg:w-1/2 lg:shrink-0">
            <div className="text-base font-semibold">Preview</div>
            <p className="text-xs text-muted-foreground">
              {merged.unique_id ?? merged.id.slice(0, 8)} will be cancelled and its child records reparented to{" "}
              {keeper.unique_id ?? keeper.id.slice(0, 8)}.
            </p>
            <OrderComparisonCard order={previewOrder} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mergeMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={mergeMutation.isPending}>
            {mergeMutation.isPending ? "Merging…" : "Confirm merge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}